"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, Sparkles, Calendar, Compass, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface PricingTier {
  id: string;
  title: string;
  badge: string;
  targetAudience: string;
  description: string;
  priceNote: string;
  isPopular: boolean;
  idealFor: string;
  deliverable1: string;
  deliverable2: string;
  deliverable3: string;
  deliverable4: string;
  deliverable5: string;
}
const tiersData: PricingTier[] = [{
  id: "strategic-advisory",
  title: "Strategic Advisory",
  badge: "For Internal Teams",
  targetAudience: "For businesses with in-house execution resources",
  description: "High-level strategic guidance and roadmap governance for teams that need clear direction.",
  priceNote: "Typical retainer from $2,000 / month",
  isPopular: false,
  idealFor: "Works well for businesses that can execute internally but need expert direction.",
  deliverable1: "Monthly strategy sessions and quarterly planning",
  deliverable2: "SEO audit and strategic roadmap development",
  deliverable3: "Keyword research and actionable content briefs",
  deliverable4: "Performance review and optimization recommendations",
  deliverable5: "Defined scope with clear, measurable deliverables"
}, {
  id: "hands-on-leadership",
  title: "Hands-On Leadership",
  badge: "Most Comprehensive",
  targetAudience: "For businesses wanting full SEO leadership",
  description: "Deep, hands-on involvement steering strategy, technical execution, and vendor teams directly.",
  priceNote: "Typical retainer from $5,000 to $10,000 / month",
  isPopular: true,
  idealFor: "Growth tier for businesses serious about scaling organic search revenue.",
  deliverable1: "Weekly collaboration and direct involvement in execution",
  deliverable2: "Technical SEO oversight, content strategy, and on-page guidance",
  deliverable3: "Vendor and freelancer oversight (writers, developers, link partners)",
  deliverable4: "Regular performance reporting tied to leads, conversions, and revenue",
  deliverable5: "Ongoing strategic leadership with direct accountability for outcomes"
}, {
  id: "project-based-consulting",
  title: "Project-Based Consulting",
  badge: "Targeted Sprints",
  targetAudience: "For specific SEO initiatives and complex transitions",
  description: "Dedicated project consulting for high-stakes site migrations, deep audits, and market launches.",
  priceNote: "Custom quote based on scope & urgency",
  isPopular: false,
  idealFor: "Right for managed migrations, pre-rebrand audits, or entering new markets.",
  deliverable1: "Website migrations, technical audits, and content gap analysis",
  deliverable2: "Targeted problem-solving for specific organic bottlenecks",
  deliverable3: "Flexible timing structured around your rollout schedule",
  deliverable4: "Engagements spanning from one day to several months",
  deliverable5: "Comprehensive transition blueprints and risk mitigation"
}];
export default function FractionalPricing() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="fractionalseopricing" data-nav="light" className="bg-background py-20 md:py-28 relative overflow-hidden">
      {/* Background Decorative Accents */}
      <div className="absolute top-0 right-1/4 size-96 bg-primary/5 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 left-10 size-80 bg-secondary/60 rounded-full blur-3xl pointer-events-none -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground">
            Direct Senior Guidance
          </Badge>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground tracking-tight mb-5">
            Fractional SEO service options
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Choose an engagement model tailored to your team and growth stage. Fractional SEO services
            typically cost between $2,000 and $10,000 monthly depending on scope and commitment.
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {tiersData.map((tier, i) => {
          const isPopular = tier.isPopular;
          return <motion.div key={tier.id} variants={cardVariants} data-index={i} className="flex flex-col h-full">
                <Card className={`relative flex flex-col justify-between h-full rounded-2xl border transition-[border-color,box-shadow] duration-300 ${isPopular ? "border-primary bg-background shadow-xl ring-2 ring-primary/20" : "border-border bg-card shadow-sm hover:border-primary/40 hover:shadow-md"}`}>
                  {isPopular && <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1 text-xs font-semibold text-primary-foreground shadow-sm">
                        <Sparkles className="size-3.5" />
                        Most Popular Engagement
                      </span>
                    </div>}

                  <div>
                    <CardHeader className="pt-8 pb-4">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <Badge variant={isPopular ? "default" : "secondary"} className={isPopular ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}>
                          {tier.badge}
                        </Badge>
                        <span className="text-xs text-muted-foreground font-medium flex items-center gap-1">
                          {tier.id === "strategic-advisory" && <Compass className="size-3.5 text-primary" />}
                          {tier.id === "hands-on-leadership" && <SafeHydrate><Calendar className="size-3.5 text-accent" /></SafeHydrate>}
                          {tier.id === "project-based-consulting" && <Layers className="size-3.5 text-primary" />}
                          Fractional Leadership
                        </span>
                      </div>

                      <CardTitle className="font-serif text-2xl font-bold text-foreground">
                        <h3 className="font-serif text-2xl font-bold text-foreground">{tier.title}</h3>
                      </CardTitle>

                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mt-1">
                        {tier.targetAudience}
                      </p>

                      <p className="text-sm text-foreground/80 mt-3 leading-relaxed">
                        {tier.description}
                      </p>

                      <div className="mt-5 pt-4 border-t border-border">
                        <div className="font-serif text-lg font-bold text-primary">
                          {tier.priceNote}
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-2 pb-6">
                      <p className="text-xs font-semibold text-foreground/70 uppercase tracking-wider mb-4">
                        What is included:
                      </p>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3 text-sm text-foreground/90">
                          <CheckCircle2 className={`size-4 shrink-0 mt-0.5 ${isPopular ? "text-primary" : "text-primary/80"}`} />
                          <span>{tier.deliverable1}</span>
                        </li>
                        <li className="flex items-start gap-3 text-sm text-foreground/90">
                          <CheckCircle2 className={`size-4 shrink-0 mt-0.5 ${isPopular ? "text-primary" : "text-primary/80"}`} />
                          <span>{tier.deliverable2}</span>
                        </li>
                        <li className="flex items-start gap-3 text-sm text-foreground/90">
                          <CheckCircle2 className={`size-4 shrink-0 mt-0.5 ${isPopular ? "text-primary" : "text-primary/80"}`} />
                          <span>{tier.deliverable3}</span>
                        </li>
                        <li className="flex items-start gap-3 text-sm text-foreground/90">
                          <CheckCircle2 className={`size-4 shrink-0 mt-0.5 ${isPopular ? "text-primary" : "text-primary/80"}`} />
                          <span>{tier.deliverable4}</span>
                        </li>
                        <li className="flex items-start gap-3 text-sm text-foreground/90">
                          <CheckCircle2 className={`size-4 shrink-0 mt-0.5 ${isPopular ? "text-primary" : "text-primary/80"}`} />
                          <span>{tier.deliverable5}</span>
                        </li>
                      </ul>

                      <div className="mt-6 p-3.5 rounded-lg bg-muted/60 border border-border/60 text-xs text-muted-foreground">
                        <strong className="text-foreground font-medium block mb-0.5">Best fit:</strong>
                        {tier.idealFor}
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-0 pb-8 px-6">
                    <Button asChild variant={isPopular ? "default" : "outline"} className={`w-full py-5 rounded-lg font-medium justify-center transition-colors ${isPopular ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-md" : "border-border hover:bg-accent hover:text-accent-foreground text-foreground"}`}>
                      <Link to="/ContactPage" className="inline-flex items-center gap-2">
                        <span>Book a Free SEO Consultation</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Reassurance Footer Banner */}
        <div className="mt-14 p-6 sm:p-8 rounded-2xl bg-muted/40 border border-border text-center max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="text-left">
            <h4 className="font-serif text-lg font-semibold text-foreground">
              Unsure which model fits your current scope?
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              Every business has distinct requirements. We can evaluate your site and tailor a transparent roadmap on an initial discovery call.
            </p>
          </div>
          <Button asChild variant="default" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/ContactPage" className="inline-flex items-center gap-2">
              <span>Discuss Your Needs</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}